import {
  PropertiesContext,
  PropertiesContextType,
} from '@/contexts/PropertiesContext'
import { useStaysProperties } from '@/hooks/stays/useStaysProperties'
import { useSyncStaysProperties } from '@/hooks/stays/useSyncStaysProperties'
import { useProperties } from '@/hooks/useProperties'
import type { PropertyFilters } from '@/lib/supabase'
import { Property, StProperty } from '@/types'
import { useDisclosure } from '@chakra-ui/react'
import React, { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface PropertiesProviderProps {
  children: ReactNode
}

export const PropertiesProvider: React.FC<PropertiesProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate()
  const [selectedStaysProperty, setSelectedStaysProperty] =
    useState<StProperty | null>(null)
  const [filters, setFilters] = useState<PropertyFilters>({
    page: 1,
    limit: 12,
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
    showDeleted: false, // Allow showing deleted properties
    // Don't filter by status by default to show all properties
  })
  const {
    isOpen: isStaysPropertiesOpen,
    onOpen: onStaysPropertiesOpen,
    onClose: onStaysPropertiesClose,
  } = useDisclosure()

  const {
    properties: listings, // Rename to avoid conflict
    count,
    isLoading,
    error,
    refetch,
    softDelete,
    restore,
    isDeleting,
    isRestoring,
  } = useProperties(filters)

  const { stProperties, isLoading: isStPropertiesLoading } =
    useStaysProperties()

  const { syncStaysProperties, isLoading: isSyncLoading } =
    useSyncStaysProperties()

  const handlePropertyClick = (id: string) => {
    navigate(`/properties/${id}`)
  }

  const handleNewProperty = (staysSynced: boolean = false) => {
    navigate(`/properties/new${staysSynced ? '-stays' : ''}`)
  }

  const refreshProperties = () => {
    refetch()
  }

  const value: PropertiesContextType = {
    // State
    filters,
    properties: (listings as Property[]) || [],
    stProperties: (stProperties as StProperty[]) || [],
    isStPropertiesLoading,
    isLoading,
    error,
    count,
    selectedStaysProperty,
    isStaysPropertiesOpen,
    isSyncLoading,

    // Actions
    setFilters,
    handlePropertyClick,
    handleNewProperty,
    refreshProperties,
    setSelectedStaysProperty,
    onStaysPropertiesOpen,
    onStaysPropertiesClose,
    syncStaysProperties,

    // CRUD Operations
    softDelete,
    restore,
    isDeleting,
    isRestoring,
  }

  return (
    <PropertiesContext.Provider value={value}>
      {children}
    </PropertiesContext.Provider>
  )
}
